/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { 
  onAuthStateChanged, 
  signOut,
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  signInAnonymously,
  GoogleAuthProvider,
  User as FirebaseUser
} from 'firebase/auth';
import { doc, onSnapshot, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { UserProfile } from '../types';

const DEV_GUEST_USER: any = {
  uid: 'guest_dev_user',
  displayName: 'Dev Guest',
  email: 'dev@example.com',
  photoURL: '',
  isAnonymous: true,
};

interface AuthContextType {
  user: FirebaseUser | null;
  globalProfile: UserProfile | null;
  loading: boolean;
  error: string | null;
  setError: (err: string | null) => void;
  logout: () => Promise<void>;
  handleLogin: (email: string, pass: string) => Promise<void>;
  handleSignup: (email: string, pass: string, name: string) => Promise<void>;
  handleGoogleLogin: () => Promise<void>;
  handleSkipLogin: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [globalProfile, setGlobalProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let unsubscribeProfile: (() => void) | undefined;
    const unsubscribeAuth = onAuthStateChanged(auth, (u) => {
      unsubscribeProfile?.();
      unsubscribeProfile = undefined;

      if (u) {
        setUser(u);
        // A delayed profile listener must not block the entire application after auth resolves.
        setLoading(false);
        if (!u.isAnonymous) {
          unsubscribeProfile = onSnapshot(doc(db, 'users', u.uid), (docSnap) => {
            if (docSnap.exists()) {
              setGlobalProfile(docSnap.data() as UserProfile);
            } else {
              setGlobalProfile(null);
            }
          }, (err) => {
            console.error("Error fetching global profile:", err);
            setLoading(false);
          });
        } else {
          setGlobalProfile(null);
        }
      } else {
        const isDev = import.meta.env.DEV || import.meta.env.VITE_IS_DEV_SERVER === 'true';
        if (isDev && localStorage.getItem('dev_skip_login') === 'true') {
          setUser(DEV_GUEST_USER);
        } else {
          setUser(null);
        }
        setGlobalProfile(null);
        setLoading(false);
      }
    });
    return () => {
      unsubscribeProfile?.();
      unsubscribeAuth();
    };
  }, []);

  const logout = async () => {
    try {
      localStorage.removeItem('dev_skip_login');
    } catch {}
    try {
      await signOut(auth);
    } catch {}
    setUser(null);
    setGlobalProfile(null);
  };

  const handleLogin = async (email: string, pass: string) => {
    try {
      setLoading(true);
      setError(null);
      await signInWithEmailAndPassword(auth, email, pass);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSignup = async (email: string, pass: string, name: string) => {
    try {
      setLoading(true);
      setError(null);
      const res = await createUserWithEmailAndPassword(auth, email, pass);
      await setDoc(doc(db, 'users', res.user.uid), {
        uid: res.user.uid,
        displayName: name,
        displayNameLower: name.toLowerCase(),
        email: email,
        photoURL: '',
        createdAt: serverTimestamp(),
        role: 'user'
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      const provider = new GoogleAuthProvider();
      const res = await signInWithPopup(auth, provider);
      
      const userDocRef = doc(db, 'users', res.user.uid);
      const userDocSnap = await getDoc(userDocRef);

      if (!userDocSnap.exists()) {
        await setDoc(userDocRef, {
          uid: res.user.uid,
          displayName: res.user.displayName || 'Anonymous',
          displayNameLower: (res.user.displayName || 'Anonymous').toLowerCase(),
          email: res.user.email,
          photoURL: res.user.photoURL || '',
          createdAt: serverTimestamp(),
          role: 'user'
        });
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSkipLogin = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInAnonymously(auth);
    } catch (err: any) {
      console.warn("signInAnonymously failed, falling back to local dev user:", err);
      setUser(DEV_GUEST_USER);
      setGlobalProfile(null);
      try {
        localStorage.setItem('dev_skip_login', 'true');
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const contextValue = React.useMemo(() => ({
    user, 
    globalProfile, 
    loading, 
    error, 
    setError, 
    logout, 
    handleLogin, 
    handleSignup, 
    handleGoogleLogin,
    handleSkipLogin
  }), [user, globalProfile, loading, error]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
